'use client';

/**
 * ActionsCell Component
 *
 * Renders row actions with inline buttons and overflow menu.
 * Supports confirmation dialogs for destructive actions.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { MoreVertical, LucideIcon } from 'lucide-react';
import { RowAction } from '../types';

interface ActionsCellProps<TData = any> {
  actions: RowAction<TData>[];
  row: TData;
  rowIndex: number;
  onAction?: (actionId: string, row: TData) => void;
  className?: string;
  /** HubTable density — smaller inline controls */
  compact?: boolean;
  /**
   * Accessible name of the overflow trigger. Defaults to "Open menu", which a
   * screen reader announces identically on every row; name the row instead.
   */
  menuLabel?: string;
}

export function ActionsCell<TData = any>({
  actions,
  row,
  rowIndex,
  onAction,
  className,
  compact = false,
  menuLabel = 'Open menu',
}: ActionsCellProps<TData>) {
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<RowAction<TData> | null>(null);

  // Filter actions by visibility (if visible function is provided)
  const visibleActions = actions.filter((action) => {
    if (action.visible && typeof action.visible === 'function') {
      return action.visible(row);
    }
    return true; // Show by default if no visible function
  });

  // Split actions into inline and overflow
  const inlineActions = visibleActions.filter((action) => !action.overflow);
  const overflowActions = visibleActions.filter((action) => action.overflow);

  const handleAction = async (action: RowAction<TData>) => {
    // Check if confirmation required
    if (action.confirm) {
      setConfirmAction(action);
      return;
    }

    await executeAction(action);
  };

  const executeAction = async (action: RowAction<TData>) => {
    setProcessingAction(action.id);
    try {
      await action.onClick(row, rowIndex);
      onAction?.(action.id, row);
    } catch (error) {
      console.error('Action error:', error);
    } finally {
      setProcessingAction(null);
      setConfirmAction(null);
    }
  };

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const isActionDisabled = (action: RowAction<TData>) => {
    if (processingAction === action.id) return true;
    if (action.disabled && typeof action.disabled === 'function') {
      return action.disabled(row);
    }
    return false;
  };

  const getActionAriaLabel = (action: RowAction<TData>): string => {
    if (typeof action.ariaLabel === 'function') {
      return action.ariaLabel(row);
    }
    return action.ariaLabel || (typeof action.label === 'function' ? action.label(row) : action.label);
  };

  const getActionClassName = (action: RowAction<TData>) => {
    switch (action.kind) {
      case 'destructive':
        return 'text-red-600 hover:text-red-700 focus:text-red-700';
      case 'primary':
        return 'text-primary-600 hover:text-primary-700';
      default:
        return 'text-slate-600 hover:text-blue-600';
    }
  };

  // Helper: resolve icon prop which may be a static LucideIcon or a (row) => LucideIcon function
  // LucideIcon components are React forwardRef objects with $$typeof, while dynamic icon functions are plain functions
  const resolveIcon = (iconProp: RowAction<TData>['icon']): LucideIcon | undefined => {
    if (!iconProp) return undefined;
    // React components (LucideIcon) have $$typeof or displayName; plain functions don't
    if (typeof iconProp === 'function' && !('displayName' in iconProp) && !('$$typeof' in iconProp)) {
      return (iconProp as (row: TData) => LucideIcon)(row);
    }
    return iconProp as LucideIcon;
  };

  return (
    <div className={cn('flex shrink-0 items-center justify-end gap-1.5', className)} onClick={stopPropagation}>
      {/* Inline Actions */}
      {inlineActions.map((action) => {
        const ResolvedIcon = resolveIcon(action.icon);
        const resolvedLabel: string = typeof action.label === 'function' ? action.label(row) : action.label;
        const disabled = isActionDisabled(action);
        const isProcessing = processingAction === action.id;

        return (
          <Button
            key={action.id}
            variant="outline"
            size="sm"
            onClick={() => handleAction(action)}
            disabled={disabled}
            className={cn(
              action.iconOnly
                ? compact
                  ? 'h-8 w-8 shrink-0 p-0 rounded-lg border-slate-300 hover:border-blue-500 hover:bg-transparent'
                  : 'h-9 w-9 p-0 rounded-lg border-slate-300 hover:border-blue-500 hover:bg-transparent'
                : compact
                  ? 'h-8 shrink-0 px-2.5 text-xs rounded-lg border-slate-300 hover:border-blue-500 hover:bg-transparent'
                  : 'h-9 px-4 text-[15px] rounded-lg border-slate-300 hover:border-blue-500 hover:bg-transparent',
              getActionClassName(action),
              disabled && 'opacity-50 cursor-not-allowed',
              action.className,
            )}
            title={getActionAriaLabel(action)}
            aria-label={getActionAriaLabel(action)}
          >
            {ResolvedIcon && (
              <ResolvedIcon
                className={cn(
                  compact ? 'h-4 w-4' : 'h-[18px] w-[18px]',
                  isProcessing && 'animate-spin',
                  !action.iconOnly && !compact && 'mr-0',
                )}
              />
            )}
            {!action.iconOnly && <span className={cn(compact ? 'ml-1.5' : 'ml-2')}>{resolvedLabel}</span>}
          </Button>
        );
      })}

      {/* Overflow Menu */}
      {overflowActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn('shrink-0 p-0', compact ? 'h-8 w-8' : 'h-8 w-8')}
              disabled={processingAction !== null}
              onClick={stopPropagation}
            >
              <MoreVertical className="h-4 w-4" aria-hidden />
              <span className="sr-only">{menuLabel}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {overflowActions.map((action, index) => {
              const ResolvedIcon = resolveIcon(action.icon);
              const resolvedLabel: string = typeof action.label === 'function' ? action.label(row) : action.label;
              const disabled = isActionDisabled(action);
              const isProcessing = processingAction === action.id;

              // Add separator before destructive actions
              const showSeparator =
                index > 0 &&
                action.kind === 'destructive' &&
                overflowActions[index - 1]?.kind !== 'destructive';

              return (
                <React.Fragment key={action.id}>
                  {showSeparator && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onClick={(event) => {
                      stopPropagation(event as any);
                      handleAction(action);
                    }}
                    disabled={disabled}
                    className={cn(
                      action.kind === 'destructive' && 'text-red-600 focus:text-red-600',
                      disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {ResolvedIcon && (
                      <ResolvedIcon className={cn('mr-2 h-4 w-4', isProcessing && 'animate-spin')} />
                    )}
                    {resolvedLabel}
                  </DropdownMenuItem>
                </React.Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <AlertDialog
          open={!!confirmAction}
          onOpenChange={(open) => !open && setConfirmAction(null)}
        >
          <AlertDialogContent onClick={stopPropagation}>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {typeof confirmAction.confirm === 'object'
                  ? confirmAction.confirm.title
                  : 'Confirm Action'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {typeof confirmAction.confirm === 'object'
                  ? confirmAction.confirm.message
                  : 'Are you sure you want to proceed with this action?'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={stopPropagation}>
                {typeof confirmAction.confirm === 'object'
                  ? confirmAction.confirm.cancelLabel || 'Cancel'
                  : 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  stopPropagation(event as any);
                  executeAction(confirmAction);
                }}
                className={cn(
                  confirmAction.kind === 'destructive' &&
                  'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                )}
              >
                {typeof confirmAction.confirm === 'object'
                  ? confirmAction.confirm.confirmLabel || 'Confirm'
                  : 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
